import React from "react";
import type { RelatorioTemposRankingLinha } from "../../../services/controleProducaoService";
import { groupByUndNome } from "../RelatorioConsumo/groupByUnd";

type RankingListProps = {
  title: string;
  hint?: string;
  rows: RelatorioTemposRankingLinha[];
  empty: string;
  onSelect?: (row: RelatorioTemposRankingLinha) => void;
};

function RankingRows({
  rows,
  onSelect,
}: {
  rows: RelatorioTemposRankingLinha[];
  onSelect?: (row: RelatorioTemposRankingLinha) => void;
}) {
  return (
    <ol className="space-y-2">
      {rows.map((row) => (
        <li
          key={`${row.undNome ?? ""}-${row.posicao}-${row.id}`}
          className="flex items-start gap-3"
        >
          <span
            className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
              row.posicao === 1
                ? "bg-primary text-white"
                : "bg-neutral-100 text-neutral-700"
            }`}
          >
            {row.posicao}
          </span>
          <span className="min-w-0 flex-1">
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(row)}
                className="block w-full truncate text-left text-sm font-medium text-primary hover:underline"
              >
                {row.nome}
              </button>
            ) : (
              <span className="block truncate text-sm font-medium text-neutral-900">
                {row.nome}
              </span>
            )}
            {row.detalhe ? (
              <span className="block text-xs text-neutral-500">
                {row.detalhe}
              </span>
            ) : null}
          </span>
          <span className="max-w-[42%] shrink-0 break-words text-right text-sm font-semibold tabular-nums text-neutral-800">
            {row.valorFormatado}
          </span>
        </li>
      ))}
    </ol>
  );
}

const RankingList: React.FC<RankingListProps> = ({
  title,
  hint,
  rows,
  empty,
  onSelect,
}) => {
  const mostrarGrupos = rows.some((row) => Boolean(row.undNome?.trim()));
  const grupos = mostrarGrupos
    ? groupByUndNome(rows, (row) => row.undNome)
    : [];

  return (
    <section className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white p-4">
      <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
      {hint ? (
        <p className="mt-0.5 text-xs text-neutral-500">{hint}</p>
      ) : null}
      {rows.length === 0 ? (
        <p className="mt-3 text-sm text-neutral-500">{empty}</p>
      ) : mostrarGrupos ? (
        <div className="mt-3 space-y-4">
          {grupos.map((grupo) => (
            <div key={grupo.undNome}>
              <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                <span className="rounded-full bg-neutral-900 px-2 py-0.5 text-[10px] font-bold text-white">
                  {grupo.undNome}
                </span>
                <span className="font-medium normal-case text-neutral-400">
                  ranking só nesta unidade
                </span>
              </p>
              <RankingRows rows={grupo.items} onSelect={onSelect} />
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-3">
          <RankingRows rows={rows} onSelect={onSelect} />
        </div>
      )}
    </section>
  );
};

export default RankingList;
